import { useState, useEffect, useCallback } from 'react';
import ApiService from '../services/api';
import { identifyUser, resetUser, trackSignUp } from '../lib/productAnalytics';
import { clearCompanionChatStorage } from '../utils/companionChatStorage';
import { safeLocalStorage } from '../utils/safeStorage';

// Centralized Auth Hook
// Responsibilities:
//  - Persist / restore current user from localStorage
//  - Provide login / signup / logout helpers
//  - Expose role helpers (isViewer / isAdmin)
//  - Track authView (login | signup)
//  - Provide isSubscribed utility (subscriptionStatus gating)
export default function useAuth() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [authView, setAuthView] = useState('login');

  // Restore from localStorage on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const saved = localStorage.getItem('testara_currentUser');
        if (!saved) return;
        const parsed = JSON.parse(saved);
        if (cancelled) return;
        setCurrentUser(parsed);
        setIsAuthenticated(true);
        // The shared demo visitor is one account for everyone; don't identify it in analytics.
        if (!parsed?.isDemo) identifyUser(parsed);
        try {
          const me = await ApiService.getMe();
          if (!cancelled && me?._id) {
            const merged = { ...parsed, ...me };
            setCurrentUser(merged);
            if (!merged?.isDemo) identifyUser(merged);
            safeLocalStorage.setItem('testara_currentUser', JSON.stringify(merged));
          }
        } catch { /* ignore */ }
      } catch {
        // ignore restore errors
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const isSubscribed = useCallback((user) => {
    const st = (user?.subscriptionStatus || '').toLowerCase();
    return st === 'active' || st === 'trialing';
  }, []);

  const login = useCallback(async (email, password) => {
    // Re-throw so callers can inspect error.code (e.g. 'email_not_verified')
    const user = await ApiService.login(email, password);
    // Tenant requires 2FA: password step passed but the user is NOT authenticated
    // yet — pass the challenge details through without persisting any auth state.
    if (user?.mfaRequired) return user;
    setCurrentUser(user);
    setIsAuthenticated(true);
    identifyUser(user);
    safeLocalStorage.setItem('testara_currentUser', JSON.stringify(user));
    return user;
  }, []);

  const loginDemo = useCallback(async () => {
    const user = await ApiService.startDemoSession();
    setCurrentUser(user);
    setIsAuthenticated(true);
    safeLocalStorage.setItem('testara_currentUser', JSON.stringify(user));
    return user;
  }, []);

  const signup = useCallback(async (userData) => {
    const user = await ApiService.signup(userData);
    if (user) {
      const method = userData?.invitationToken ? 'invitation' : 'email';
      trackSignUp({
        method,
        userId: user._id || user.id || user.email,
      });
    }
    // Campaign / flag-off: server issued a session — enter the app immediately.
    if (user?.token && user?.verificationRequired === false) {
      setCurrentUser(user);
      setIsAuthenticated(true);
      identifyUser(user);
      safeLocalStorage.setItem('testara_currentUser', JSON.stringify(user));
    }
    return user;
  }, []);

  const logout = useCallback(() => {
    const clearLocal = () => {
      resetUser();
      setIsAuthenticated(false);
      setCurrentUser(null);
      try { localStorage.removeItem('testara_currentUser'); } catch { /* ignore */ }
      try {
        Object.keys(localStorage)
          .filter((k) => k.startsWith('testara_tasks'))
          .forEach((k) => localStorage.removeItem(k));
      } catch { /* ignore */ }
      try { clearCompanionChatStorage(); } catch { /* ignore */ }
      try { ApiService.clearToken?.(); } catch { /* ignore */ }
      try { ApiService.clearActiveApiKey?.(); } catch { /* ignore */ }
    };
    // Await audit POST while JWT is still present, then clear local session.
    // Cap wait so a slow/offline API cannot block sign-out.
    // Allow enough time for server logout to suspend MCP connections before JWT is cleared.
    Promise.race([
      Promise.resolve(ApiService.logout?.()).catch(() => {}),
      new Promise((r) => setTimeout(r, 2500)),
    ]).finally(clearLocal);
  }, []);

  const isViewer = (currentUser?.role || '').toLowerCase() === 'viewer';
  const isEditor = (currentUser?.role || '').toLowerCase() === 'editor';
  const isAdmin = (currentUser?.role || '').toLowerCase() === 'admin';

  return {
    // state
    isAuthenticated,
    currentUser,
    authView,
    // setters
    setAuthView,
    setCurrentUser,
    setIsAuthenticated, // ADDED: allow components to directly toggle auth state when needed
    // actions
    login,
    loginDemo,
    signup,
    logout,
    // helpers
    isViewer,
    isEditor,
    isAdmin,
    isSubscribed
  };
}
